import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { type ColumnDef, type PaginationState } from "@tanstack/react-table";
import { Copy, Trash2, RotateCcwKey } from "lucide-react";
import ApiKeySuccessDialog from "./components/ApiKeySuccessDialog";
import ApiKeysEmptyState from "./components/ApiKeysEmptyState";
import ApiKeysToolbar from "./components/ApiKeysToolbar";
import { cn } from "@/utils/cn";
import { recordTable } from "@/components/tables/table-presentation";
import DataTable from "@/components/DataTable";
import CreateApiKeyDialog from "./components/CreateApiKeyDialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useApiKeys,
  useDeleteApiKey,
  useRotateApiKey,
} from "./hooks/useApiKeys";
import type { ApiKey, ApiKeyWithValue } from "./service";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useProjects } from "@/contexts/ProjectContext";
import { useProjectsQuery } from "@/features/projects/hooks/useProjectsQuery";
import { maskApiKeyForList } from "./api-key-display";

const EMPTY_API_KEYS: ApiKey[] = [];

// For formatting dates
const formatDate = (
  dateString: string | null | undefined,
  neverLabel = "Never",
) => {
  if (!dateString) return neverLabel;
  return new Date(dateString).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const isApiKeyExpired = (apiKey: ApiKey) => {
  if (!apiKey.expires_at) return false;

  const expiresAt = new Date(apiKey.expires_at).getTime();
  return !Number.isNaN(expiresAt) && expiresAt <= Date.now();
};

const ApiKeys = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const createDialogTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [confirmAction, setConfirmAction] = useState<{
    type: "delete" | "rotate";
    apiKey: ApiKey;
  } | null>(null);
  const [rotatedApiKey, setRotatedApiKey] = useState<ApiKeyWithValue | null>(
    null,
  );

  const handleCreateDialogOpen = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      createDialogTriggerRef.current = event.currentTarget;
      setCreateDialogOpen(true);
    },
    [],
  );

  const { selectedProjects } = useProjects();
  const { data: projects = [] } = useProjectsQuery();
  const globallyScopedProjectIds = useMemo(
    () =>
      Array.isArray(selectedProjects) && !selectedProjects.includes("all")
        ? selectedProjects
        : projects.map((project) => project.id),
    [projects, selectedProjects],
  );
  const projectOptions = useMemo(
    () =>
      projects.filter((project) =>
        globallyScopedProjectIds.includes(project.id),
      ),
    [globallyScopedProjectIds, projects],
  );
  const projectIds =
    projectFilter !== "all" &&
    globallyScopedProjectIds.includes(projectFilter)
      ? [projectFilter]
      : globallyScopedProjectIds;

  useEffect(() => {
    if (
      projectFilter !== "all" &&
      !globallyScopedProjectIds.includes(projectFilter)
    ) {
      // oxlint-disable-next-line react/set-state-in-effect -- external project scope invalidated the local filter
      setProjectFilter("all");
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    }
  }, [globallyScopedProjectIds, projectFilter]);

  const {
    data: apiKeysResponse,
    isLoading,
    error,
    isFetching,
  } = useApiKeys({
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    projectIds,
  });
  const deleteMutation = useDeleteApiKey();
  const rotateMutation = useRotateApiKey();
  const apiKeys = apiKeysResponse?.items ?? EMPTY_API_KEYS;
  const totalItems = apiKeysResponse?.metadata.total_items ?? 0;

  const handleKeyCreated = useCallback(() => {
    // Rely on React Query's onSuccess in the hook to invalidate
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  }, []);

  const handleProjectFilterChange = useCallback((value: string) => {
    setProjectFilter(value);
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchQuery("");
    setProjectFilter("all");
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  }, []);

  const handleCopyText = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      toast.success(t("common.copiedToClipboard"));
    },
    [t],
  );

  const handleDelete = useCallback((apiKey: ApiKey) => {
    setConfirmAction({ type: "delete", apiKey });
  }, []);

  const handleRotate = useCallback((apiKey: ApiKey) => {
    setConfirmAction({ type: "rotate", apiKey });
  }, []);

  const handleConfirmAction = useCallback(() => {
    if (!confirmAction) {
      return;
    }

    const closeDialog = () => setConfirmAction(null);

    if (confirmAction.type === "delete") {
      deleteMutation.mutate(confirmAction.apiKey.id, {
        onSuccess: () => {
          closeDialog();
        },
      });
      return;
    }

    if (confirmAction.type === "rotate") {
      rotateMutation.mutate(confirmAction.apiKey.id, {
        onSuccess: (rotatedKey) => {
          setRotatedApiKey(rotatedKey.new_key);
          closeDialog();
        },
      });
      return;
    }
  }, [confirmAction, deleteMutation, rotateMutation]);

  const isConfirmingAction =
    deleteMutation.isPending || rotateMutation.isPending;
  const pendingDeleteId = deleteMutation.isPending
    ? deleteMutation.variables
    : null;
  const pendingRotateId = rotateMutation.isPending
    ? rotateMutation.variables
    : null;
  const isDeleteConfirmation = confirmAction?.type === "delete";
  const isRotateConfirmation = confirmAction?.type === "rotate";
  const confirmActionLabel = isDeleteConfirmation
    ? t("apiKeys.deleteKey")
    : t("apiKeys.rotateKey");
  const pendingConfirmActionLabel = isDeleteConfirmation
    ? t("common.deleting")
    : t("common.rotating");

  const filteredData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return apiKeys.filter((key) => {
      return (
        !query ||
        key.name.toLowerCase().includes(query) ||
        key.id.toLowerCase().includes(query) ||
        key.key?.toLowerCase().includes(query) ||
        key.project_name?.toLowerCase().includes(query) ||
        key.project_id?.toLowerCase().includes(query)
      );
    });
  }, [searchQuery, apiKeys]);
  const filtersActive = searchQuery.trim().length > 0 || projectFilter !== "all";
  const listPending = isLoading || isFetching;
  const showInitialEmpty =
    !listPending && !error && totalItems === 0 && !filtersActive;
  const showNoResults =
    !listPending && !error && filtersActive && filteredData.length === 0;

  const columns: ColumnDef<ApiKey>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        header: t("apiKeys.keyName"),
        minSize: 200,
        cell: ({ row }) => (
          <div>
            <span className={recordTable.primary}>
              {row.original.name}
            </span>
            <span className={cn(recordTable.secondary, "font-mono")}>
              {row.original.id}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "key",
        header: t("apiKeys.secretKey"),
        minSize: 140,
        cell: ({ row }) => (
          <span
            className={
              row.original.key
                ? "font-mono text-sm text-foreground"
                : "text-sm italic text-muted-foreground"
            }
          >
            {maskApiKeyForList(
              row.original.key ?? row.original.secret_key,
            ) || t("common.notAvailable")}
          </span>
        ),
      },
      {
        accessorKey: "project_name",
        header: t("apiKeys.projectName"),
        minSize: 200,
        cell: ({ row }) => (
          <span
            className={
              row.original.project_name
                ? "text-sm text-foreground"
                : "text-sm italic text-muted-foreground"
            }
          >
            {row.original.project_name || t("common.notAvailable")}
          </span>
        ),
      },
      {
        accessorKey: "expires_at",
        header: t("apiKeys.expires"),
        minSize: 140,
        sortingFn: (rowA, rowB, columnId) => {
          const a = rowA.getValue(columnId) as string | null;
          const b = rowB.getValue(columnId) as string | null;
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;
          return new Date(a).getTime() - new Date(b).getTime();
        },
        cell: ({ row }) => (
          <span
            className={
              !row.original.expires_at
                ? "text-sm italic text-muted-foreground"
                : "text-sm text-foreground"
            }
          >
            {formatDate(row.original.expires_at, t("common.never"))}
          </span>
        ),
      },
      {
        accessorKey: "created_at",
        header: t("common.created"),
        minSize: 140,
        cell: ({ row }) => (
          <div>
            <span className="block text-sm text-foreground">
              {formatDate(row.original.created_at)}
            </span>
          </div>
        ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">{t("common.actions")}</span>,
        enableSorting: false,
        minSize: 132,
        cell: ({ row }) => (
          <div className={recordTable.actions}>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(recordTable.action, "hover:text-primary")}
              onClick={(e) => {
                e.stopPropagation();
                handleCopyText(row.original.id);
              }}
              aria-label={t("apiKeys.copyId")}
              title={t("apiKeys.copyId")}
            >
              <Copy aria-hidden="true" className="size-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(recordTable.action, "hover:text-primary")}
              onClick={(e) => {
                e.stopPropagation();
                handleRotate(row.original);
              }}
              aria-label={t("apiKeys.rotateKey")}
              title={t("apiKeys.rotateKey")}
              disabled={!row.original.is_active || rotateMutation.isPending}
              aria-busy={pendingRotateId === row.original.id}
            >
              {pendingRotateId === row.original.id ? (
                <Spinner className="size-4" />
              ) : (
                <RotateCcwKey aria-hidden="true" className="size-4" />
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(recordTable.action, "hover:text-destructive")}
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.original);
              }}
              aria-label={t("apiKeys.deleteKey")}
              title={t("apiKeys.deleteKey")}
              disabled={isConfirmingAction}
              aria-busy={pendingDeleteId === row.original.id}
            >
              {pendingDeleteId === row.original.id ? (
                <Spinner className="size-4" />
              ) : (
                <Trash2 aria-hidden="true" className="size-4" />
              )}
            </Button>
          </div>
        ),
      },
    ],
    [
      handleCopyText,
      handleDelete,
      handleRotate,
      isConfirmingAction,
      pendingDeleteId,
      pendingRotateId,
      rotateMutation.isPending,
      t,
    ],
  );

  return (
    <div className="px-4 py-8 sm:px-6">
      {error && (
        <div className="mb-4 rounded-md bg-destructive/15 p-3 text-sm text-destructive">
          {t("apiKeys.failedToLoad")}
        </div>
      )}

      {showInitialEmpty ? (
        <ApiKeysEmptyState
          variant="initial"
          onAction={handleCreateDialogOpen}
        />
      ) : (
        <>
          <ApiKeysToolbar
            searchQuery={searchQuery}
            projectFilter={projectFilter}
            projects={projectOptions}
            disabled={listPending}
            onSearchChange={handleSearchChange}
            onProjectFilterChange={handleProjectFilterChange}
            onCreate={handleCreateDialogOpen}
          />

          {showNoResults ? (
            <ApiKeysEmptyState
              variant="filtered"
              onAction={handleClearFilters}
            />
          ) : (
            <DataTable<ApiKey>
              columns={columns}
              data={filteredData}
              totalCount={totalItems}
              manualPagination
              loading={listPending}
              pagination={pagination}
              onPaginationChange={setPagination}
              rowsPerPageOptions={[10, 25, 50]}
              emptyMessage={t("apiKeys.noKeysFound")}
              getRowClassName={(apiKey) =>
                isApiKeyExpired(apiKey)
                  ? "bg-muted/45 text-muted-foreground opacity-70 hover:bg-muted/55 dark:bg-muted/20 dark:opacity-60 dark:hover:bg-muted/25 [&_td]:text-muted-foreground [&_span]:text-muted-foreground [&_button]:text-muted-foreground/70"
                  : undefined
              }
            />
          )}
        </>
      )}

      <CreateApiKeyDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onKeyCreated={handleKeyCreated}
        returnFocusRef={createDialogTriggerRef}
      />

      <AlertDialog
        open={confirmAction !== null}
        onOpenChange={(open) => {
          if (!open && !isConfirmingAction) {
            setConfirmAction(null);
          }
        }}
      >
        <AlertDialogContent className="border-border bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              {isRotateConfirmation
                ? t("apiKeys.rotateApiKey")
                : t("apiKeys.deleteApiKey")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isRotateConfirmation
                ? t("apiKeys.rotateConfirm", {
                    name: confirmAction.apiKey.name,
                  })
                : t("apiKeys.deleteConfirm", {
                    name: confirmAction?.apiKey.name,
                  })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-border bg-card text-foreground hover:bg-accent"
              disabled={isConfirmingAction}
            >
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              className={
                confirmAction?.type === "delete"
                  ? "bg-destructive text-white hover:bg-destructive/90"
                  : "bg-primary text-primary-foreground hover:bg-primary/90"
              }
              disabled={isConfirmingAction}
              aria-busy={isConfirmingAction}
            >
              {isConfirmingAction ? (
                <>
                  <Spinner className="size-4" />
                  <span>{pendingConfirmActionLabel}</span>
                </>
              ) : (
                confirmActionLabel
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ApiKeySuccessDialog
        open={rotatedApiKey !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRotatedApiKey(null);
          }
        }}
        title={t("apiKeys.keyRotated")}
        apiKey={rotatedApiKey}
        note={t("apiKeys.saveKeyNote")}
      />
    </div>
  );
};

export default ApiKeys;
